import type { QuoteDetail, RepairServiceComponent } from '@techgitter/contracts';
import { optionQty, optionLineSellDisplay } from './quotePricing';
import {
  resolveBillingGroups,
  groupLineName,
  groupLineDescription,
  isEffectivelyConsolidated,
} from './quoteBillingGroups';
import {
  repairServiceBlurb,
  withCatalogDescription,
} from './repairServiceBlurb';

/**
 * One projected customer-facing line, shared by the "Generate invoice" and
 * "Generate ticket" dialogs. `sellPrice` is stored ex-GST *before* the ×1.1
 * GST multiply (matching how the backend bills each line) — call `toDisplay`
 * to get the value shown in the quote's GST mode.
 */
export interface PromotionPreviewLine {
  description: string;
  productCode: string;
  sellPrice: number;
  quantity?: number;
  /**
   * Stable key identifying which underlying service/item this line
   * collapses from, used to send a transient per-line description
   * override on promotion (see `QuotePromoteRequest.lineDescriptions`).
   * Present only on editable customer-facing lines:
   *   - service-shaped: the `RepairService.id`;
   *   - itemised: the `QuoteItem.id`;
   *   - consolidated: the sentinel `"consolidated"`.
   * Undefined on system-generated credit lines (bundle discount,
   * diagnostics reimbursement), which must not be overridden.
   */
  lineKey?: string;
}

export interface QuotePromotionPreview {
  lines: PromotionPreviewLine[];
  mode: 'ex' | 'inc';
  gstLabel: string;
  eftActive: boolean;
  /** e.g. ", incl. 1.11% EFT buffer" — appended to price labels. */
  eftSuffix: string;
  /** Convert a stored ex-GST line value to the displayed (rounded) amount. */
  toDisplay: (exGst: number) => number;
  /** A line's displayed (rounded) amount — negative lines skip the EFT buffer. */
  lineDisplay: (line: PromotionPreviewLine) => number;
  /** Sum of each line's displayed (rounded) value. */
  total: number;
}

/**
 * Build the line-item preview a quote projects onto its resulting invoice OR
 * ticket. Mirrors the backend's promotion projection exactly (marked items /
 * selected options, service-shaped collapse, bundle discount, diagnostic-fee
 * reimbursement, two-step GST rounding, and the invisible EFT-fee buffer —
 * applied to every positive line, never a discount or credit) so
 * the preview matches what RepairShopr will charge. Pure — no I/O, no hooks —
 * so it can back either dialog.
 */
export function buildQuotePromotionPreview(
  quote: QuoteDetail,
  /** The RS product's catalog description for a SKU, when the catalog is loaded. */
  catalogDescriptionFor: (sku: string) => string | undefined = () => undefined,
): QuotePromotionPreview {
  const isServiceShaped =
    quote.type === 'repair' || quote.type === 'network_install';
  const mode: 'ex' | 'inc' = quote.priceDisplayMode ?? 'ex';
  const round2 = (n: number): number => Math.round(n * 100) / 100;
  const eftActive = quote.includeEftFee && quote.eftFeeRate > 0;
  const eftMultiplier = eftActive ? 1 + quote.eftFeeRate : 1;
  // inc-GST = round(ex * 1.1, 2); final = round(inc-GST * (1+rate), 2).
  // ex-GST display = final / 1.1 (what RS shows in its ex-GST cell).
  const displayWith = (exGst: number, eft: boolean): number => {
    const incGst = round2(exGst * 1.1);
    const finalIncGst = eft ? round2(incGst * eftMultiplier) : incGst;
    return mode === 'inc' ? finalIncGst : round2(finalIncGst / 1.1);
  };
  const toDisplay = (exGst: number): number => displayWith(exGst, eftActive);
  // Negative lines (bundle discount, diagnostics credit, any negative item)
  // are never uplifted — `_bake_in_eft_fee` skips them.
  const lineDisplay = (line: PromotionPreviewLine): number =>
    displayWith(line.sellPrice, eftActive && line.sellPrice >= 0);
  const gstLabel = mode === 'inc' ? 'inc GST' : 'ex GST';
  const eftSuffix = eftActive
    ? `, incl. ${(quote.eftFeeRate * 100).toFixed(2)}% EFT buffer`
    : '';

  const lines: PromotionPreviewLine[] = [];

  if (isServiceShaped) {
    const contributes = (c: RepairServiceComponent): boolean =>
      c.kind === 'third_party' ? c.vendorQuoteStatus === 'accepted' : true;
    const sorted = [...(quote.repairServices ?? [])].sort(
      (a, b) => (a.position ?? 0) - (b.position ?? 0),
    );
    // Same rules as sm4rt-api's `_build_service_orchestrator_lines`. A repair
    // quote's consolidated description is its composed Job Summary: it leads
    // the FIRST service line as "Diagnostics: …", and a service with no
    // description of its own names itself rather than repeating the summary.
    const summary = (quote.consolidatedDescription ?? '').trim();
    const isRepair = quote.type === 'repair';
    const fallbackDesc =
      summary || (quote.name ?? '').trim() || 'Repair Service';
    let diagnosticsPending = isRepair && summary.length > 0;
    for (const svc of sorted) {
      const sell = (svc.components ?? []).reduce(
        (sum, c) =>
          contributes(c) && Number.isFinite(c.sellPrice)
            ? sum + c.sellPrice
            : sum,
        0,
      );
      if ((svc.components ?? []).length === 0) continue;
      const ownDesc =
        (svc.description ?? '').trim() ||
        (isRepair
          ? (svc.repairServiceName ?? '').trim() || svc.repairServiceCode
          : fallbackDesc);
      // The product's catalog description, then the service's quote blurb
      // (Quote and Invoice Preview Options included).
      let description = withCatalogDescription(
        catalogDescriptionFor(svc.repairServiceCode),
        repairServiceBlurb(svc, ownDesc),
      );
      if (diagnosticsPending && !svc.isStandalonePart) {
        description = `Diagnostics: ${summary}\n${description}`;
        diagnosticsPending = false;
      }
      lines.push({
        description,
        productCode: svc.repairServiceCode,
        sellPrice: sell,
        lineKey: svc.id,
      });
    }
    const bundle = quote.bundleDiscount;
    if (bundle && Number.isFinite(bundle.amount) && bundle.amount > 0) {
      lines.push({
        description: bundle.description || 'Bundle discount',
        productCode: '',
        sellPrice: -bundle.amount,
      });
    }
  } else {
    // Billing groups collapse to one line each (name + Includes + summed
    // ex-GST); ungrouped marked items bill 1:1. Grouped lines are keyed by the
    // group id (so their description can be overridden on promotion).
    const groups = resolveBillingGroups(quote.items);
    const groupByFirstMember = new Map(groups.map((g) => [g.items[0].id, g]));
    const groupedIds = new Set(groups.flatMap((g) => g.items.map((i) => i.id)));
    // A consolidated quote collapses everything into ONE line server-side
    // (keyed "consolidated"), so per-line keys wouldn't line up — read-only.
    const effConsolidated = isEffectivelyConsolidated(quote);
    const sorted = [...quote.items].sort(
      (a, b) => (a.position ?? 0) - (b.position ?? 0),
    );
    for (const item of sorted) {
      if (!item.markedForInvoice || !item.selectedOptionId) continue;
      if (groupedIds.has(item.id)) {
        const g = groupByFirstMember.get(item.id);
        if (!g) continue; // non-first member
        const sellExGst = g.items.reduce((sum, it) => {
          const o = it.options.find((oo) => oo.id === it.selectedOptionId);
          return o ? sum + optionLineSellDisplay(o, 'inc') / 1.1 : sum;
        }, 0);
        lines.push({
          description: groupLineDescription(g),
          productCode: groupLineName(g),
          sellPrice: round2(sellExGst),
          quantity: 1,
          lineKey: effConsolidated ? undefined : g.id,
        });
        continue;
      }
      const opt = item.options.find((o) => o.id === item.selectedOptionId);
      if (!opt) continue;
      lines.push({
        description: opt.description,
        productCode: opt.productCode,
        sellPrice: optionLineSellDisplay(opt, 'inc') / 1.1,
        quantity: optionQty(opt),
        lineKey: effConsolidated ? undefined : item.id,
      });
    }
  }

  // Diagnostic-fee reimbursement (server-derived, inc-GST) — divide out GST so
  // `toDisplay` (which grosses ex-GST up by 1.1) keeps the total correct. Skip
  // when there are no other lines yet, so a lone credit line doesn't mask the
  // "no items marked" empty state.
  const diag = quote.diagnosticReimbursement;
  if (
    diag &&
    Number.isFinite(diag.amount) &&
    diag.amount > 0 &&
    lines.length > 0
  ) {
    lines.push({
      description: `Reimbursement of diagnostics fee paid on invoice ${diag.sourceInvoiceNumber}`,
      productCode: 'DISC.Diags',
      sellPrice: -diag.amount / 1.1,
    });
  }

  const total = lines.reduce((acc, l) => acc + lineDisplay(l), 0);

  return {
    lines,
    mode,
    gstLabel,
    eftActive,
    eftSuffix,
    toDisplay,
    lineDisplay,
    total,
  };
}
